import type { Occurrence, OccurrenceStatus, Payment, PaymentStatus } from '@klopotun/contracts';
import { formatCivilDate, formatMoney, toCivilDate } from '@klopotun/domain';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  type BankMatch,
  type BankMerchantLink,
  usePaymentBankLinksQuery,
  useUnlinkPaymentMerchantMutation,
} from '../../entities/bank-match/index.js';
import { useCardsQuery } from '../../entities/card/index.js';
import { useCategoriesQuery } from '../../entities/category/index.js';
import { useMeQuery } from '../../entities/me/index.js';
import {
  useOccurrencesQuery,
  useOccurrenceStatusMutation,
  type OccurrenceStatusInput,
} from '../../entities/occurrence/index.js';
import {
  nextDueDate,
  useDeletePaymentMutation,
  useMarkPaymentSeenMutation,
  usePaymentQuery,
  usePaymentStatusMutation,
} from '../../entities/payment/index.js';
import { occurrencePayPath, paymentEditPath } from '../../features/navigation/index.js';
import {
  earliestDueDateByPayment,
  formatPaidTotal,
  paidTotalsByPayment,
} from '../../features/occurrence-view/index.js';
import {
  formatMonthName,
  formatPaymentAmount,
  formatPaymentKind,
  formatPaymentStatus,
  formatRecurrence,
  formatStoredDateForPeople,
} from '../../features/payment-view/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import {
  Button,
  ErrorState,
  List,
  ListItem,
  Screen,
  Sheet,
  Spinner,
  type SheetAction,
} from '../../shared/ui/index.js';
import { BankMerchants } from './bank-merchants.js';
import styles from './payment-detail-page.module.css';
import { PeriodHistory } from './period-history/period-history.js';

const STATE_ACTION_LABEL: Record<PaymentStatus, string> = {
  active: 'Пауза',
  paused: 'Поновити',
  cancelled: 'Повернути',
};

type PeriodActionHandlers = {
  onPay: () => void;
  onUnpay: () => void;
  onUnmatch: () => void;
  onVoid: () => void;
  onRestore: () => void;
};

/**
 * Every action the server allows out of each state (design note D2), and none it does not: an
 * unpay on a planned period or a restore on a paid one answers 409.
 */
const periodActionsFor = (
  status: OccurrenceStatus,
  isBankMatched: boolean,
  handlers: PeriodActionHandlers,
): SheetAction[] => {
  const voidManually: SheetAction = {
    id: 'void',
    label: 'Не було платежу',
    danger: true,
    onSelect: handlers.onVoid,
  };
  const restore: SheetAction = {
    id: 'restore',
    label: 'Повернути в план',
    onSelect: handlers.onRestore,
  };

  if (status === 'paid' && isBankMatched) {
    return [
      {
        id: 'unmatch',
        label: 'Скасувати зіставлення з банку',
        danger: true,
        onSelect: handlers.onUnmatch,
      },
    ];
  }

  if (status === 'paid') {
    return [
      { id: 'correct', label: 'Виправити оплату', onSelect: handlers.onPay },
      { id: 'unpay', label: 'Відкотити оплату', danger: true, onSelect: handlers.onUnpay },
    ];
  }

  if (status === 'planned') {
    return [{ id: 'pay', label: 'Підтвердити оплату', onSelect: handlers.onPay }, voidManually];
  }

  if (status === 'paused') {
    return [restore, voidManually];
  }

  return [restore];
};

const periodTitleOf = (period: Occurrence, match: BankMatch | undefined): string => {
  const dueDate = formatStoredDateForPeople(period.dueDate);
  const isPaidFromBank = match !== undefined && period.status === 'paid';

  if (!isPaidFromBank) {
    return dueDate;
  }

  const description = match.description.trim();

  return description === '' ? `${dueDate} · з банку` : `${dueDate} · з банку: ${description}`;
};

const LoadingScreen = () => (
  <Screen>
    <div className={styles.centered}>
      <Spinner label="Завантаження" />
    </div>
  </Screen>
);

type FailureScreenProps = {
  message: string;
  onRetry: () => void;
};

const FailureScreen = ({ message, onRetry }: FailureScreenProps) => (
  <Screen>
    <ErrorState title="Не вдалося завантажити підписку" message={message} onRetry={onRetry} />
  </Screen>
);

const PENDING_VALUE = '…';

const FAILED_VALUE = 'не завантажилось';

type LinkedFact = {
  label: string | null;
  isPending: boolean;
  isFailed: boolean;
};

/**
 * A failed request is not an empty value: without the middle case the screen would state
 * "без картки" for a payment that has one, purely because the card listing did not arrive.
 */
const linkedValueOf = (fact: LinkedFact, emptyText: string): string => {
  if (fact.isPending) {
    return PENDING_VALUE;
  }

  if (fact.isFailed) {
    return FAILED_VALUE;
  }

  return fact.label ?? emptyText;
};

type PaymentFactsProps = {
  payment: Payment;
  nextDue: LinkedFact;
  /** What the payment has cost so far, in the currency the totals are kept in; empty when nothing was paid. */
  paidTotal: string | null;
  card: LinkedFact;
  category: LinkedFact;
};

const priceChangeTextOf = (payment: Payment): string | undefined => {
  const change = payment.priceChange;

  if (change === null) {
    return undefined;
  }

  const previous = formatMoney({
    amountMinor: change.previousAmountMinor,
    currency: payment.currency,
  });
  const changedOn = formatStoredDateForPeople(change.changedOn);

  if (payment.amountMinor === null) {
    return `Ціна змінилась ${changedOn}: раніше було ${previous}.`;
  }

  const current = formatMoney({ amountMinor: payment.amountMinor, currency: payment.currency });

  return `Ціна змінилась з ${previous} на ${current} (${changedOn}).`;
};

const PaymentFacts = ({ payment, nextDue, paidTotal, card, category }: PaymentFactsProps) => (
  <>
    <List header="Головне" footer={priceChangeTextOf(payment)}>
      <ListItem title="Стан" value={formatPaymentStatus(payment.status)} />
      {payment.status === 'cancelled' && payment.cancelReason !== null && (
        <ListItem title="Причина скасування" subtitle={payment.cancelReason} isSecondLineWrapping />
      )}
      <ListItem title="Сума" value={formatPaymentAmount(payment)} />
      <ListItem title="Спосіб оплати" value={formatPaymentKind(payment.kind)} />
      <ListItem title="Періодичність" value={formatRecurrence(payment)} />
      <ListItem title="Число місяця" value={String(payment.anchorDay)} />
      {payment.anchorMonth !== null && (
        <ListItem title="Місяць" value={formatMonthName(payment.anchorMonth)} />
      )}
      <ListItem title="Наступний термін" value={linkedValueOf(nextDue, 'немає')} />
      {paidTotal !== null && <ListItem title="Сплачено" value={paidTotal} />}
    </List>

    <List header="Умови">
      <ListItem title="Початок" value={formatStoredDateForPeople(payment.startDate)} />
      <ListItem
        title="Кінець"
        value={payment.endDate === null ? 'без кінця' : formatStoredDateForPeople(payment.endDate)}
      />
      <ListItem title="Картка" value={linkedValueOf(card, 'без картки')} />
      <ListItem title="Категорія" value={linkedValueOf(category, 'без категорії')} />
    </List>

    {payment.status === 'paused' && (
      <List header="Пауза">
        <ListItem
          title="На паузі з"
          value={
            payment.pausedFrom === null ? 'невідомо' : formatStoredDateForPeople(payment.pausedFrom)
          }
        />
        <ListItem
          title="Поновлення"
          value={
            payment.resumeOn === null
              ? 'без визначеної дати'
              : formatStoredDateForPeople(payment.resumeOn)
          }
        />
      </List>
    )}

    {payment.note !== null && (
      <section className={styles.note}>
        <h2 className={styles.sectionTitle}>Нотатка</h2>
        <p className={styles.noteText}>{payment.note}</p>
      </section>
    )}

    {payment.cancelUrl !== null && (
      <a className={styles.link} href={payment.cancelUrl} target="_blank" rel="noreferrer">
        Як скасувати цю підписку
      </a>
    )}
  </>
);

export const PaymentDetailPage = () => {
  const [isCancelAsked, setCancelAsked] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<Occurrence | null>(null);
  const [periodToUnmatch, setPeriodToUnmatch] = useState<Occurrence | null>(null);
  const [isNotSubscriptionAsked, setNotSubscriptionAsked] = useState(false);
  const [merchantToUnlink, setMerchantToUnlink] = useState<BankMerchantLink | null>(null);
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const me = useMeQuery();
  const payment = usePaymentQuery(id);
  const periods = useOccurrencesQuery({ paymentId: id });
  const cards = useCardsQuery({ includeArchived: true });
  const categories = useCategoriesQuery({ includeArchived: true });
  const statusMutation = usePaymentStatusMutation();
  const periodMutation = useOccurrenceStatusMutation();
  const bankLinks = usePaymentBankLinksQuery(id);
  const unlinkMutation = useUnlinkPaymentMerchantMutation();
  const deleteMutation = useDeletePaymentMutation();
  const { mutate: markSeen } = useMarkPaymentSeenMutation();
  const isNewPayment = payment.data?.isNew === true;
  const goBack = useCallback(() => {
    if (periodToUnmatch !== null) {
      setPeriodToUnmatch(null);

      return;
    }

    if (selectedPeriod !== null) {
      setSelectedPeriod(null);

      return;
    }

    if (isCancelAsked) {
      setCancelAsked(false);

      return;
    }

    if (isNotSubscriptionAsked) {
      setNotSubscriptionAsked(false);

      return;
    }

    if (merchantToUnlink !== null) {
      setMerchantToUnlink(null);

      return;
    }

    void navigate(-1);
  }, [
    periodToUnmatch,
    selectedPeriod,
    isCancelAsked,
    isNotSubscriptionAsked,
    merchantToUnlink,
    navigate,
  ]);

  useBackButton(goBack);

  useEffect(() => {
    if (!isNewPayment) {
      return;
    }

    markSeen(id);
  }, [isNewPayment, id, markSeen]);

  if (payment.isPending) {
    return <LoadingScreen />;
  }

  if (payment.isError) {
    return (
      <FailureScreen
        message={payment.error.message}
        onRetry={() => {
          void payment.refetch();
        }}
      />
    );
  }

  if (me.isPending) {
    return <LoadingScreen />;
  }

  if (me.isError) {
    return (
      <FailureScreen
        message={me.error.message}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  const settings = me.data.settings;
  const timeZone = settings.timezone;
  const card = cards.data?.find((candidate) => candidate.id === payment.data.cardId);
  const category = categories.data?.find((candidate) => candidate.id === payment.data.categoryId);
  // Materialised periods stop at the horizon, so a yearly payment has no row ahead of it for most
  // of the year. Without the forecast the screen would state "немає" for a live payment whose next
  // date is simply further out than the rows reach.
  const plannedDue = earliestDueDateByPayment(periods.data ?? []).get(id);
  const forecastDue = nextDueDate(payment.data, new Date(), timeZone);
  const nextDue = plannedDue ?? (forecastDue === null ? null : formatCivilDate(forecastDue));
  const paidTotal = formatPaidTotal(paidTotalsByPayment(periods.data ?? []).get(id));
  const actionFailure =
    statusMutation.error ?? periodMutation.error ?? unlinkMutation.error ?? deleteMutation.error;
  const bankMerchants = bankLinks.data?.merchants ?? [];
  const bankMatchById = new Map(
    (bankLinks.data?.matches ?? []).map((match) => [match.occurrenceId, match] as const),
  );
  const bankMatchedIds = new Set(bankMatchById.keys());
  const selectedMatch = selectedPeriod === null ? undefined : bankMatchById.get(selectedPeriod.id);

  const changePeriod = (input: OccurrenceStatusInput): void => {
    statusMutation.reset();
    unlinkMutation.reset();
    periodMutation.mutate(input, {
      onSuccess: () => {
        setSelectedPeriod(null);
        setPeriodToUnmatch(null);
      },
      // The sheet would otherwise stay open on top of the error text drawn underneath it, and the
      // tap would look like it did nothing at all.
      onError: () => {
        setSelectedPeriod(null);
        setPeriodToUnmatch(null);
      },
    });
  };

  const pause = (): void => {
    const today = formatCivilDate(toCivilDate(new Date(), timeZone));

    periodMutation.reset();
    unlinkMutation.reset();
    statusMutation.mutate({ action: 'pause', id, body: { pausedFrom: today, resumeOn: null } });
  };

  const resume = (): void => {
    periodMutation.reset();
    unlinkMutation.reset();
    statusMutation.mutate({ action: 'resume', id });
  };

  const cancel = (): void => {
    periodMutation.reset();
    unlinkMutation.reset();
    statusMutation.mutate(
      { action: 'cancel', id, body: { reason: null } },
      {
        onSuccess: () => {
          setCancelAsked(false);
        },
        // The sheet would otherwise stay open on top of the error text drawn underneath it, and
        // the tap would look like it did nothing at all.
        onError: () => {
          setCancelAsked(false);
        },
      },
    );
  };

  const unlink = (linkId: string): void => {
    statusMutation.reset();
    periodMutation.reset();
    deleteMutation.reset();
    unlinkMutation.mutate(linkId);
  };

  const removeAsNotSubscription = (): void => {
    statusMutation.reset();
    periodMutation.reset();
    unlinkMutation.reset();
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setNotSubscriptionAsked(false);
        void navigate(-1);
      },
      onError: () => {
        setNotSubscriptionAsked(false);
      },
    });
  };

  const notSubscriptionConfirmation: SheetAction[] = [
    {
      id: 'confirm-not-subscription',
      label: 'Так, це не підписка',
      danger: true,
      disabled: deleteMutation.isPending,
      onSelect: removeAsNotSubscription,
    },
  ];

  const cancelConfirmation: SheetAction[] = [
    {
      id: 'confirm-cancel',
      label: 'Так, скасувати підписку',
      danger: true,
      disabled: statusMutation.isPending,
      onSelect: cancel,
    },
  ];

  const periodActions: SheetAction[] =
    selectedPeriod === null
      ? []
      : periodActionsFor(selectedPeriod.status, selectedMatch !== undefined, {
          onPay: () => {
            setSelectedPeriod(null);
            void navigate(occurrencePayPath(selectedPeriod.id));
          },
          onUnpay: () => {
            changePeriod({ action: 'unpay', id: selectedPeriod.id });
          },
          onUnmatch: () => {
            setSelectedPeriod(null);
            setPeriodToUnmatch(selectedPeriod);
          },
          onVoid: () => {
            changePeriod({ action: 'void', id: selectedPeriod.id });
          },
          onRestore: () => {
            changePeriod({ action: 'restore', id: selectedPeriod.id });
          },
        }).map((action) => ({ ...action, disabled: periodMutation.isPending }));

  const unmatchConfirmation: SheetAction[] =
    periodToUnmatch === null
      ? []
      : [
          {
            id: 'confirm-unmatch',
            label: 'Так, скасувати зіставлення',
            danger: true,
            disabled: periodMutation.isPending,
            onSelect: () => {
              changePeriod({ action: 'unpay', id: periodToUnmatch.id });
            },
          },
        ];

  return (
    <Screen>
      <div className={styles.content}>
        <header className={styles.header}>
          <h1 className={styles.title}>{payment.data.name}</h1>
        </header>

        <div className={styles.quickActions}>
          <Button
            variant="secondary"
            onClick={() => {
              void navigate(paymentEditPath(id));
            }}
          >
            Редагувати
          </Button>
          <Button
            variant="secondary"
            busy={statusMutation.isPending}
            onClick={payment.data.status === 'active' ? pause : resume}
          >
            {STATE_ACTION_LABEL[payment.data.status]}
          </Button>
        </div>

        {actionFailure !== null && (
          <p className={styles.actionError} role="alert">
            {actionFailure.message}
          </p>
        )}

        <PaymentFacts
          payment={payment.data}
          nextDue={{
            label: nextDue === null ? null : formatStoredDateForPeople(nextDue),
            isPending: periods.isPending,
            isFailed: periods.isError,
          }}
          paidTotal={paidTotal}
          card={{ label: card?.label ?? null, isPending: cards.isPending, isFailed: cards.isError }}
          category={{
            label: category?.name ?? null,
            isPending: categories.isPending,
            isFailed: categories.isError,
          }}
        />

        {bankLinks.isError && (
          <p className={styles.actionError} role="alert">
            Дані банку не завантажилися — позначки «з банку» не показуємо
          </p>
        )}

        {bankMerchants.length > 0 && (
          <BankMerchants
            merchants={bankMerchants}
            chosenMerchant={merchantToUnlink}
            isUnlinking={unlinkMutation.isPending}
            onChoose={setMerchantToUnlink}
            onDismiss={() => {
              setMerchantToUnlink(null);
            }}
            onUnlink={unlink}
          />
        )}

        <PeriodHistory
          occurrences={periods.data}
          kind={payment.data.kind}
          isFromBank={payment.data.isFromBank}
          settings={settings}
          bankMatchedIds={bankMatchedIds}
          isPending={periods.isPending}
          errorMessage={periods.error === null ? null : periods.error.message}
          onRetry={() => {
            void periods.refetch();
          }}
          onSelect={setSelectedPeriod}
        />

        <div className={styles.dangerActions}>
          {payment.data.status !== 'cancelled' && (
            <Button
              variant="secondaryDanger"
              disabled={statusMutation.isPending || deleteMutation.isPending}
              onClick={() => {
                setCancelAsked(true);
              }}
            >
              Скасувати підписку
            </Button>
          )}
          <Button
            variant="secondaryDanger"
            disabled={statusMutation.isPending || deleteMutation.isPending}
            onClick={() => {
              setNotSubscriptionAsked(true);
            }}
          >
            Це не підписка
          </Button>
        </div>
      </div>

      <Sheet
        open={isCancelAsked}
        actions={cancelConfirmation}
        title="Скасувати підписку? Кнопка «назад» це не поверне."
        closeLabel="Ні, лишити"
        onClose={() => {
          setCancelAsked(false);
        }}
      />

      <Sheet
        open={isNotSubscriptionAsked}
        actions={notSubscriptionConfirmation}
        title="Це не підписка? Її буде видалено разом з історією, а місяці, які підтвердив банк, повернуться у звичайні витрати. Цей продавець більше не стане підпискою."
        closeLabel="Ні, лишити"
        onClose={() => {
          setNotSubscriptionAsked(false);
        }}
      />

      <Sheet
        open={selectedPeriod !== null}
        actions={periodActions}
        title={selectedPeriod === null ? undefined : periodTitleOf(selectedPeriod, selectedMatch)}
        onClose={() => {
          setSelectedPeriod(null);
        }}
      />

      <Sheet
        open={periodToUnmatch !== null}
        actions={unmatchConfirmation}
        title="Скасувати зіставлення? Платіж повернеться в план, а ця операція сама вже нічого не підтверджуватиме — прив'язати її знову можна на екрані операції у «Витратах»."
        closeLabel="Ні, лишити"
        onClose={() => {
          setPeriodToUnmatch(null);
        }}
      />
    </Screen>
  );
};
